import { fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";
import React, { useEffect, useState } from "react";
import { clearAuthToken, setAuthToken } from "../../services/api";
import SyncStatus from "../SyncStatus";
import ForgotPasswordForm from "./ForgotPasswordForm";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";

interface AuthWrapperProps {
  children: React.ReactNode;
}

type AuthState = "loading" | "authenticated" | "unauthenticated";
type AuthMode = "login" | "signup" | "forgot-password";

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [user, setUser] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();

      // Get the JWT token from the current session
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        // Set the token in the API service for authenticated requests
        setAuthToken(idToken);
        console.log("✅ Authentication token set for API requests");
      } else {
        console.warn("⚠️ No ID token found in session");
      }

      setUser(currentUser);
      setAuthState("authenticated");
    } catch (error) {
      console.log("User not authenticated:", error);
      clearAuthToken(); // Clear any existing token
      setAuthState("unauthenticated");
    }
  };

  const handleAuthSuccess = () => {
    setSuccessMessage("");
    checkAuthState(); // This will set the auth token
  };

  const handlePasswordResetSuccess = () => {
    setSuccessMessage(
      "Password reset successful! You can now sign in with your new password."
    );
    setAuthMode("login");
    setTimeout(() => setSuccessMessage(""), 5000);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      clearAuthToken(); // Clear the auth token
      setUser(null);
      setAuthState("unauthenticated");
      setAuthMode("login");
      setSuccessMessage("");
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  if (authState === "loading") {
    return (
      <div className="min-h-screen bg-dark-bg text-dark-text flex flex-col justify-center items-center p-4 font-sans">
        <div className="text-center">
          <svg
            className="animate-spin mx-auto h-12 w-12 text-accent"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="mt-4 text-dark-textSecondary font-medium">
            Loading Farm Tasks...
          </p>
        </div>
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return (
      <div className="min-h-screen bg-dark-bg text-dark-text flex flex-col justify-center items-center font-sans">
        {successMessage && (
          <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm p-4">
            <div className="bg-green-600 text-white p-3 rounded-md shadow-lg flex items-center">
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm font-medium">{successMessage}</p>
            </div>
          </div>
        )}
        {authMode === "signup" ? (
          <SignupForm
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={() => {
              setAuthMode("login");
              setSuccessMessage("");
            }}
          />
        ) : authMode === "forgot-password" ? (
          <ForgotPasswordForm
            onSuccess={handlePasswordResetSuccess}
            onSwitchToLogin={() => {
              setAuthMode("login");
              setSuccessMessage("");
            }}
          />
        ) : (
          <LoginForm
            onSuccess={handleAuthSuccess}
            onSwitchToSignup={() => {
              setAuthMode("signup");
              setSuccessMessage("");
            }}
            onSwitchToForgotPassword={() => {
              setAuthMode("forgot-password");
              setSuccessMessage("");
            }}
          />
        )}
      </div>
    );
  }

  // Authenticated - show the main app
  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <nav className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center">
              <svg
                className="h-8 w-auto text-green-600 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 17v-2a4 4 0 00-4-4H2a2 2 0 00-2 2v2a2 2 0 002 2h1M15 17v-2a4 4 0 014-4h1a2 2 0 012 2v2a2 2 0 01-2 2h-1M9 11V9a4 4 0 014-4h0a4 4 0 014 4v2m-6 8h2c1.105 0 2-.895 2 2 2z"
                ></path>
              </svg>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
                Farm Tasks
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <SyncStatus />
              <div className="hidden sm:block">
                <span className="text-xs sm:text-sm text-gray-600 font-medium">
                  {user?.signInDetails?.loginId || user?.email}
                </span>
              </div>
              <button
                onClick={handleSignOut}
                className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <span className="hidden sm:inline">Sign Out</span>
                <span className="sm:hidden">Out</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="pb-safe">{children}</main>
    </div>
  );
};

export default AuthWrapper;
